// Slice 45 — Retention policy tab. Six rows (the policy fields + the
// floor-locked audit_years), each with an Edit-modal that PUTs the
// `retention_policy` row.

import { useEffect, useState } from "react";
import { RetentionApi, type RetentionPolicy } from "../../api/client";
import { useShell } from "../../shell/HqShellContext";
import { HqBadge, HqButton, HqCard, HqInput, HqModal, Icons } from "../../ui";
import { fmtDateMs } from "./format";

interface Field {
  key: keyof Omit<RetentionPolicy, "id" | "updatedAt" | "updatedBy">;
  label: string;
  unit: string;
  description: string;
  min: number;
  max: number;
}

const FIELDS: Field[] = [
  {
    key: "feedbackDays",
    label: "Feedback retention",
    unit: "days",
    description: "Feedback rows older than this are deleted by the nightly job.",
    min: 30,
    max: 1825,
  },
  {
    key: "telemetryDays",
    label: "Telemetry retention",
    unit: "days",
    description: "Telemetry counter rows older than this are deleted by the nightly job.",
    min: 30,
    max: 1825,
  },
  {
    key: "dsrClosedDays",
    label: "Closed-DSR retention",
    unit: "days",
    description: "Fulfilled/rejected DSRs older than this are deleted (separate from audit-log).",
    min: 90,
    max: 3650,
  },
  {
    key: "ipHashDays",
    label: "IP-hash clearing",
    unit: "days",
    description: "audit_log.ip_hash + sessions.ip_hash are nulled past this age (spec §5.6).",
    min: 1,
    max: 730,
  },
  {
    key: "auditYears",
    label: "Audit-log retention",
    unit: "years",
    description:
      "Floor-locked ≥ 7 (Art. 5(2) accountability). Audit rows themselves are NEVER deleted by the nightly job.",
    min: 7,
    max: 30,
  },
];

export interface RetentionTabProps {
  retention: RetentionPolicy | null;
  onMutated: () => void;
}

export function RetentionTab({ retention, onMutated }: RetentionTabProps) {
  const [editing, setEditing] = useState<Field | null>(null);

  if (!retention) {
    return (
      <HqCard title="Retention policy" icon={Icons.Shield}>
        <p
          style={{
            margin: 0,
            color: "var(--hq-text-dim)",
            fontSize: 13,
            textAlign: "center",
            padding: "16px 0",
          }}
        >
          Loading…
        </p>
      </HqCard>
    );
  }

  return (
    <HqCard
      title="Retention policy"
      icon={Icons.Shield}
      right={
        <span className="hq-mono" style={{ fontSize: 10.5, color: "var(--hq-text-dim)" }}>
          updated {fmtDateMs(retention.updatedAt)} by {retention.updatedBy}
        </span>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {FIELDS.map((f) => (
          <Row
            key={f.key}
            field={f}
            value={retention[f.key]}
            isFloorLocked={f.key === "auditYears"}
            onEdit={() => setEditing(f)}
          />
        ))}
        <div
          style={{
            marginTop: 8,
            padding: 12,
            background: "var(--hq-bg-input)",
            border: "1px solid var(--hq-border-input)",
            borderRadius: "var(--hq-radius-md)",
            fontSize: 11.5,
            color: "var(--hq-text-muted)",
            lineHeight: 1.6,
          }}
        >
          <strong>Nightly job:</strong> 03:00 UTC, idempotent. Deletes feedback / telemetry /
          closed-DSRs past their windows; clears <code>ip_hash</code> older than{" "}
          {retention.ipHashDays} days on audit + sessions; flags DSRs past <code>warn_at</code>.{" "}
          <strong>Never</strong> touches <code>audit_log</code> inside the {retention.auditYears}
          -year floor.
        </div>
      </div>
      <EditModal
        field={editing}
        current={retention}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          onMutated();
        }}
      />
    </HqCard>
  );
}

function Row({
  field,
  value,
  isFloorLocked,
  onEdit,
}: {
  field: Field;
  value: number;
  isFloorLocked: boolean;
  onEdit: () => void;
}) {
  return (
    <div
      style={{
        padding: 12,
        background: "var(--hq-bg-input)",
        border: "1px solid var(--hq-border-input)",
        borderRadius: "var(--hq-radius-md)",
        display: "flex",
        alignItems: "center",
        gap: 12,
      }}
    >
      <div style={{ flex: 1 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 12.5, fontWeight: 500, color: "var(--hq-text)" }}>
            {field.label}
          </span>
          {isFloorLocked && (
            <HqBadge tone="warn" size="sm">
              floor ≥ 7
            </HqBadge>
          )}
        </div>
        <div style={{ fontSize: 11, color: "var(--hq-text-dim)", marginTop: 2, lineHeight: 1.45 }}>
          {field.description}
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span
          className="hq-mono"
          style={{ fontSize: 18, fontWeight: 600, color: "var(--hq-text)" }}
        >
          {value}
        </span>
        <span className="hq-mono" style={{ fontSize: 10, color: "var(--hq-text-dim)" }}>
          {field.unit}
        </span>
        <HqButton variant="ghost" size="sm" onClick={onEdit}>
          Edit
        </HqButton>
      </div>
    </div>
  );
}

function EditModal({
  field,
  current,
  onClose,
  onSaved,
}: {
  field: Field | null;
  current: RetentionPolicy;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { toast } = useShell();
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (field) setValue(String(current[field.key]));
  }, [field, current]);

  if (!field) return null;

  async function submit() {
    if (!field) return;
    const n = Number(value);
    if (!Number.isFinite(n) || n < field.min || n > field.max) {
      setErr(`Must be an integer in [${field.min}, ${field.max}].`);
      return;
    }
    setBusy(true);
    setErr(null);
    const res = await RetentionApi.update({
      [field.key]: Math.floor(n),
    } as Partial<RetentionPolicy>);
    setBusy(false);
    if (!res.ok) {
      setErr(res.error.message);
      return;
    }
    toast(`${field.label} updated.`, "success");
    onSaved();
  }

  return (
    <HqModal
      open
      onClose={onClose}
      title={`Edit · ${field.label}`}
      width={440}
      footer={
        <>
          <HqButton variant="ghost" onClick={onClose}>
            Cancel
          </HqButton>
          <HqButton
            variant="primary"
            leadingIcon={Icons.Check}
            loading={busy}
            onClick={() => void submit()}
          >
            Save
          </HqButton>
        </>
      }
    >
      <p
        style={{
          margin: "0 0 12px",
          fontSize: 12.5,
          color: "var(--hq-text-muted)",
          lineHeight: 1.55,
        }}
      >
        {field.description}
      </p>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <HqInput
          value={value}
          onChange={(e) => setValue(e.target.value)}
          type="number"
          mono
          autoFocus
        />
        <span className="hq-mono" style={{ fontSize: 11, color: "var(--hq-text-dim)" }}>
          {field.unit}
        </span>
      </div>
      <div style={{ marginTop: 6, fontSize: 10.5, color: "var(--hq-text-dim)" }}>
        Allowed range: {field.min} – {field.max} {field.unit}.
      </div>
      {err && (
        <div
          style={{
            marginTop: 10,
            padding: "8px 12px",
            borderRadius: "var(--hq-radius-md)",
            background: "var(--hq-danger-soft)",
            color: "var(--hq-danger)",
            fontSize: 12,
            border: "1px solid rgba(251,113,133,.25)",
          }}
        >
          {err}
        </div>
      )}
    </HqModal>
  );
}
